import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, FormHelperText, Typography, useTheme } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Input from "@components/Input/Input";
import { getSideDrawerStyles } from "@components/SideDrawer/SideDrawer.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import type { MovementError } from "@features/vessel-call/vessel-call-status/MovementServices/form/utils.ts";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import type { Movement } from "@services/movement-services/types";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import z from "zod";
import { useCompleteMovement } from "../hooks/useCompleteMovement";

// This form does not have a store defined, so all fields will be managed via local state or props
// => use regular input components and manage their state locally or via props
interface DisputeMovementServiceProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  currentMovements: Movement[];
}
interface OutletContextType {
  vesselCallDetails: { id: string; vesselId: string };
}
const serviceVesselCall = "bff-vessel-call";
const DisputeMovementService: React.FC<DisputeMovementServiceProps> = ({
  isOpen,
  onClose,
  currentMovements,
}) => {
  const queryClient = useQueryClient();
  const { vesselCallDetails } = useOutletContext<OutletContextType>();
  const vesselId = vesselCallDetails?.vesselId;
  const vesselCallId = vesselCallDetails?.id;
  const currentMovement = currentMovements?.[0];
  const { t } = useTranslation();
  const [disputeReason, setDisputeReason] = useState("");
  const [error, setError] = useState<null | string>(null);
  const [disputeAttachmentsUrl, setDisputeAttachmentsUrl] = useState<string>();
  const [additionalErrors, setAdditionalErrors] = useState<{
    disputeReason?: string;
    disputeFiles?: string;
  }>({});
  const isPrintableKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
  const TEXTAREA_MAX_CHARS = 1000;
  const theme = useTheme();
  const disputeReasonSchema = z.object({
    disputeReason: z
      .string()
      .min(
        1,
        t("form-validation.required", {
          label: t("vessel-management.vessel-status-page.rejectReason"),
        }),
      )
      .max(
        1000,
        t("form-validation.max-length", {
          label: t("vessel-management.vessel-status-page.rejectReason"),
          max: 1000,
        }),
      ),
  });
  const inputSlotProps = {
    root: {
      className: `rounded-lg text-sm px-4 py-2 border`,
      style: {
        borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
        color: theme.palette.text.primary,
      },
    },
    htmlInput: {
      maxLength: TEXTAREA_MAX_CHARS,
    },
  };

  const sxProps = {
    "& .MuiInputBase-root": {
      borderRadius: "8px",
      borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
      padding: "12px",
      fontSize: theme.typography.body1.fontSize,
      color: theme.palette.text.primary,
    },
    "& textarea": {
      padding: 0,
    },
  };
  const styles = getSideDrawerStyles(theme);
  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };
  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const disputeAttachmentFile = useUploadStore(
    (state) => state.filesByKey.disputeAttachmentFile ?? EMPTY_UPLOAD_ENTRIES,
  );
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const profile = FILE_UPLOAD_PROFILES.sideDrawerApprovalFile;
  const allowedSideDrawerExtensions = profile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );
  const getFileSignature = (entry: UploadEntry | undefined) => {
    if (!(entry instanceof File)) return null;
    return `${entry.name}-${entry.lastModified}-${entry.size}`;
  };
  const triggerUpload = useCallback(
    (
      entry: UploadEntry | undefined,
      handler: ((file: File) => void) | undefined,
      tracker: React.MutableRefObject<string | null>,
    ) => {
      if (!handler) return;

      if (!(entry instanceof File)) return;

      const signature = getFileSignature(entry);

      if (!signature) return;

      if (tracker.current === signature) return;

      tracker.current = signature;
      handler(entry);
    },
    [],
  );
  const { notify } = useNotification();
  const disputeUploadSignatureRef = useRef<string | null>(null);
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);
  const handleFileUpload = ({
    newFiles,
    setUploadErrors,
    setUploadFiles,
    onUploadDocument,
    uploadTracker,
    errorKey,
  }: {
    newFiles: File[];
    setUploadErrors: (
      value:
        | {
            disputeFiles?: string;
          }
        | ((prev: { disputeFiles?: string }) => {
            disputeFiles?: string;
          }),
    ) => void;
    setUploadFiles: (files: UploadEntry[]) => void;
    onUploadDocument?: (file: File) => void;
    uploadTracker: React.MutableRefObject<string | null>;
    errorKey: "disputeFiles";
  }) => {
    if (newFiles.length > 1) {
      setUploadErrors({ [errorKey]: t("upload.one-document") });
      return;
    }

    const file = newFiles[0];
    if (!file) {
      return;
    }

    setUploadErrors((prev) => ({ ...prev, [errorKey]: undefined }));
    setUploadFiles([file]);
    triggerUpload(file, onUploadDocument, uploadTracker);
  };
  const documentUploadMutation = useMutation({
    mutationFn: ({ file, documentType }: { file: File; documentType: string }) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "DisputeMovementService",
          action: "upload_movement_service_document",
          entityType: "movement_service_document",
        },
        async (transactionId) => {
          const payloadForSasToken = {
            prefix: SasTokenPrefix.VESSEL_REGISTRATION,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };

          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );
          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: documentType,
          });

          const documentUrl = uploadResult?.documentUrl ?? blobUrl;
          return { documentUrl, documentType };
        },
      ),
    onError: (error: Error) => setError(error.message),
  });
  const setSideDrawerAdditionalFile = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("sideDrawerAdditionalFile");
        return;
      }
      setFilesForKey("sideDrawerAdditionalFile", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const setDisputeAttachmentFile = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("disputeAttachmentFile");
        return;
      }
      setFilesForKey("disputeAttachmentFile", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  useEffect(() => {
    setSideDrawerAdditionalFile([]);
    setDisputeAttachmentFile([]);
  }, []);

  const resetFormValues = () => {
    setDisputeReason("");
    setAdditionalErrors({});
    setDisputeAttachmentsUrl("");
    setDisputeAttachmentFile([]);
    setSideDrawerAdditionalFile([]);
  };

  const handleDocumentUpload = (file: File, documentType: string) => {
    documentUploadMutation.mutate(
      { file, documentType },
      {
        onSuccess: ({
          documentUrl,
          documentType: currentType,
        }: {
          documentUrl: string;
          documentType: string;
        }) => {
          setError(null);
          if (currentType === "DISPUTE_NOTES") {
            setDisputeAttachmentsUrl(documentUrl);
          }
        },
      },
    );
  };
  const handleSuccess = async () => {
    onClose("disputeDrawer");
    resetFormValues();
    notify({
      title: t("vessel-call.movement-services.movement-dispute-rejected"),
      type: "success",
    });
    setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
      });
    }, 2000);
  };

  const handleError = (error: MovementError) => {
    queryClient.invalidateQueries({
      queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
    });
    onClose("disputeDrawer");
    resetFormValues();
    notify({
      title: t("common.error"),
      description:
        error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
      type: "error",
    });
  };

  const { completeMovement } = useCompleteMovement({
    handleSuccess,
    handleError,
  });

  const handleSubmit = async () => {
    const reasonResult = disputeReasonSchema.safeParse({ disputeReason });
    setAdditionalErrors((prev) => ({
      ...prev,
      disputeReason: reasonResult.success
        ? undefined
        : reasonResult.error.flatten().fieldErrors.disputeReason?.[0],
      disputeFiles: undefined,
    }));
    if (!reasonResult.success) {
      return;
    }
    const payload: {
      status: "DISPUTED";
      rejectionReason?: string;
      rejectionReasonAttachments?: [
        {
          originalFileName: string;
          url: string;
          type: string;
        },
      ];
    } = {
      status: "DISPUTED",
    };

    // Always include the rejection reason (already validated)
    payload.rejectionReason = disputeReason;
    if (disputeAttachmentsUrl?.length) {
      payload.rejectionReasonAttachments = [
        {
          originalFileName: disputeAttachmentFile[0]?.name || "attachment",
          url: disputeAttachmentsUrl,
          type: "REJECT_ATTACHMENTS",
        },
      ];
    }

    await completeMovement({
      id: currentMovement.id || "",
      service: serviceVesselCall,
      payload,
    });
  };

  return (
    <div data-testid="reject-movement-service-form">
      <SideDrawer
        isOpen={isOpen}
        title={t(`vessel-call.movement-services.reject-title`)}
        onClose={() => {
          onClose("disputeDrawer");
          resetFormValues();
        }}
        submitLabel={t(`vessel-call.movement-services.reject-button`)}
        onSubmit={handleSubmit}
      >
        <div className="px-6">
          <Typography
            variant="body1"
            data-testid="movement-service-form-title"
            sx={{ color: "text.primary", pb: 4 }}
          >
            {t(`vessel-call.movement-services.reject-subtitle`)}
          </Typography>
          <div className="col-span-full mb-0 flex flex-col gap-2 mt-6">
            <CustomFormLabel>
              {t("vessel-management.vessel-status-page.rejectReason")}
            </CustomFormLabel>
            <Input
              placeholder={t("vessel-management.vessel-status-page.rejectReason")}
              multiline
              minRows={4}
              fullWidth
              variant="outlined"
              value={disputeReason}
              onKeyDown={(e) => {
                if (!isPrintableKey(e)) return;
                if (disputeReason.length >= TEXTAREA_MAX_CHARS) {
                  setAdditionalErrors((prev) => ({
                    ...prev,
                    disputeReason: t("form-validation.max-length", {
                      label: t("vessel-management.vessel-status-page.rejectReason"),
                      max: TEXTAREA_MAX_CHARS,
                    }),
                  }));
                }
              }}
              onBlur={() => {
                setAdditionalErrors((prev) => ({
                  ...prev,
                  additionalDescription: undefined,
                }));
              }}
              onChange={(e) => {
                const value = e.target.value;
                setDisputeReason(value);
                const result = disputeReasonSchema.safeParse({
                  disputeReason: value,
                });
                setAdditionalErrors((prev) => ({
                  ...prev,
                  disputeReason: result.success
                    ? undefined
                    : result.error.flatten().fieldErrors.disputeReason?.[0],
                }));
              }}
              slotProps={inputSlotProps}
              sx={sxProps}
              testId={"additional-notes-input"}
            />
            {additionalErrors.disputeReason && (
              <p style={styles.errorText} className="text-sm mt-1">
                {additionalErrors.disputeReason}
              </p>
            )}
            <FormHelperText sx={charCounterSx}>
              {disputeReason.length}/{TEXTAREA_MAX_CHARS}
            </FormHelperText>
          </div>
          <div className="col-span-full mt-6 ">
            <input type="hidden" name="disputeAttachmentFileUrl" />
            <div
              className={
                additionalErrors.disputeFiles ? "border border-red-500 rounded-md p-1" : ""
              }
            >
              <DocumentUpload
                label={t("vessel-management.vessel-status-page.documents")}
                testId={"additional-file-upload"}
                name="disputeAttachmentFile"
                isSideDrawer
                maxFiles={1}
                allowedExtensions={allowedSideDrawerExtensions}
                onUpload={(newFiles) =>
                  handleFileUpload({
                    newFiles,
                    setUploadErrors: setAdditionalErrors,
                    setUploadFiles: setDisputeAttachmentFile,
                    onUploadDocument: (file) => handleDocumentUpload(file, "DISPUTE_NOTES"),
                    uploadTracker: disputeUploadSignatureRef,
                    errorKey: "disputeFiles",
                  })
                }
                conditions={FILE_UPLOAD_PROFILES.sideDrawerApprovalFile}
                files={disputeAttachmentFile}
                setFiles={setDisputeAttachmentFile}
                uploadDescription={t("upload.dragOrDropDescription")}
              />
            </div>
            {additionalErrors.disputeFiles && (
              <p style={styles.errorText} className="text-sm mt-1">
                {additionalErrors.disputeFiles}
              </p>
            )}
            {disputeAttachmentFile[0] && !additionalErrors.disputeFiles && (
              <div className="flex-1 mt-4">
                <SingleFile
                  file={disputeAttachmentFile[0]}
                  fieldName="disputeAttachmentFile"
                  setFiles={() => {
                    setAdditionalErrors((prev) => ({ ...prev, disputeFiles: undefined }));
                    setDisputeAttachmentFile([]);
                    disputeUploadSignatureRef.current = null;
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </SideDrawer>
      {error ? <Alert severity="error">{error}</Alert> : null}
    </div>
  );
};

export default DisputeMovementService;
